import React from "react";
import {
  Smile, Sparkles, TrendingUp, Clock,
  Truck, Building2, PackageOpen, ClipboardCheck, Recycle, Boxes, Home as HomeIcon,
  ShieldCheck, BadgeDollarSign, Users, FileCheck2, MapPin,
  Quote, Star, ArrowRight, CheckCircle2, Phone, Mail,
} from "lucide-react";
import { Navbar } from "../components/site/Navbar";
import { Footer } from "../components/site/Footer";
import { ContactForm } from "../components/site/ContactForm";
import { useReveal } from "../components/site/useReveal";
import {
  COMPANY, IMAGES, BENEFITS, SERVICES, WHY, AREAS, TESTIMONIALS,
} from "../data";

const ICONS = {
  Smile, Sparkles, TrendingUp, Clock, Truck, Building2, PackageOpen,
  ClipboardCheck, Recycle, Boxes, Home: HomeIcon, ShieldCheck,
  BadgeDollarSign, Users, FileCheck2, MapPin,
};

const SectionLabel = ({ children }) => (
  <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#2563EB] mb-3">
    {children}
  </span>
);

export default function Home() {
  useReveal();

  return (
    <div className="bg-white overflow-x-hidden">
      <Navbar />

      {/* ===== HERO ===== */}
      <section id="home" className="relative min-h-screen flex items-center pt-20">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.hero}
            alt="Modern apartment community"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A]/95 via-[#0F172A]/80 to-[#1E3A8A]/50" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur border border-white/15 px-4 py-1.5 text-sm font-medium text-blue-100 mb-7">
              <MapPin className="h-4 w-4" /> Serving the Greater Savannah Area
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.07]">
              Professional Junk Removal for Homes & Apartments — Plus Reliable Door-to-Door Trash Pickup for Apartment Communities
            </h1>
            <p className="mt-6 text-lg text-slate-200 max-w-2xl leading-relaxed">
              Helping apartment communities across Savannah, Pooler, Port
              Wentworth, and Richmond Hill maintain cleaner properties and
              happier residents.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <a
                href="#contact"
                data-testid="hero-cta-button"
                className="inline-flex items-center justify-center gap-2 rounded-md bg-[#2563EB] px-7 py-4 text-base font-semibold text-white hover:bg-[#1D4ED8] transition-colors shadow-lg"
              >
                Request a Free Proposal <ArrowRight className="h-5 w-5" />
              </a>
              <a
                href={`tel:${COMPANY.phonePrimaryRaw}`}
                data-testid="hero-phone-button"
                className="inline-flex items-center justify-center gap-2 rounded-md bg-white/10 backdrop-blur border border-white/20 px-7 py-4 text-base font-semibold text-white hover:bg-white/20 transition-colors"
              >
                <Phone className="h-5 w-5" /> {COMPANY.phonePrimary}
              </a>
            </div>
            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-slate-300">
              {["Fully Insured", "Locally Owned", "5 Nights a Week"].map((t) => (
                <span key={t} className="inline-flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-[#3B82F6]" /> {t}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== COMPANY OVERVIEW ===== */}
      <section className="py-20 md:py-28 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-20 items-center">
            <div className="reveal">
              <SectionLabel>Who We Are</SectionLabel>
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#1E293B] leading-tight">
                A premium valet trash partner built for property managers
              </h2>
              <p className="mt-6 text-[#475569] leading-relaxed">
                Coastal Valet Trash provides dependable, doorstep waste
                collection designed exclusively for apartment communities,
                senior living, and student housing across the Savannah region.
                We combine professional, uniformed staff with consistent
                service so your residents enjoy a cleaner, more convenient home.
              </p>
              <p className="mt-4 text-[#475569] leading-relaxed">
                As a locally owned and operated business, we treat every
                property like our own — keeping your grounds spotless,
                inspection-ready, and your residents happy.
              </p>
              <div className="mt-8 grid grid-cols-3 gap-6">
                {[
                  { n: "5x", l: "Weekly Pickups" },
                  { n: "100%", l: "Insured" },
                  { n: "24h", l: "Response Time" },
                ].map((s) => (
                  <div key={s.l}>
                    <div className="font-display text-3xl font-extrabold text-[#1E3A8A]">
                      {s.n}
                    </div>
                    <div className="text-sm text-[#475569] mt-1">{s.l}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="reveal relative">
              <img
                src={IMAGES.worker}
                alt="Professional valet trash worker"
                className="rounded-2xl shadow-2xl object-cover w-full h-[480px]"
              />
              <div className="absolute -bottom-6 -left-6 hidden sm:flex items-center gap-3 rounded-xl bg-white shadow-xl border border-gray-100 px-5 py-4">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-[#2563EB] text-white">
                  <ShieldCheck className="h-6 w-6" />
                </span>
                <div>
                  <div className="font-display font-bold text-[#1E293B]">Trusted & Insured</div>
                  <div className="text-xs text-[#475569]">Professional collection team</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SERVICES ===== */}
      <section id="services" className="py-20 md:py-28 bg-[#0F172A]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl reveal">
            <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#60A5FA] mb-3">
              Our Services
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-white">
              Complete waste solutions for multi-family properties
            </h2>
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => {
              const Icon = ICONS[s.icon];
              return (
                <div
                  key={s.title}
                  data-testid={`service-card-${i}`}
                  className="reveal group rounded-xl bg-white/[0.04] border border-white/10 p-7 hover:bg-white/[0.08] hover:border-[#2563EB]/50 transition-all duration-300"
                  style={{ transitionDelay: `${i * 60}ms` }}
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-[#2563EB] text-white">
                    <Icon className="h-6 w-6" />
                  </span>
                  <h3 className="mt-5 font-display text-xl font-bold text-white">
                    {s.title}
                  </h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">
                    {s.text}
                  </p>
                </div>
              );
            })}
            <div className="reveal rounded-xl bg-gradient-to-br from-[#2563EB] to-[#1E3A8A] p-7 flex flex-col justify-between">
              <div>
                <h3 className="font-display text-xl font-bold text-white">
                  Need a custom program?
                </h3>
                <p className="mt-2 text-sm text-blue-100">
                  We tailor waste management solutions to fit your property's
                  exact needs and budget.
                </p>
              </div>
              <a
                href="#contact"
                data-testid="services-cta-button"
                className="mt-6 inline-flex items-center gap-2 rounded-md bg-white px-5 py-3 text-sm font-semibold text-[#1E3A8A] hover:bg-blue-50 transition-colors w-fit"
              >
                Get a Free Proposal <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ===== BENEFITS ===== */}
      <section className="py-20 md:py-28 bg-[#F8FAFC]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl reveal">
            <SectionLabel>The Benefits</SectionLabel>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#1E293B]">
              Why valet trash matters for your community
            </h2>
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {BENEFITS.map((b, i) => {
              const Icon = ICONS[b.icon];
              return (
                <div
                  key={b.title}
                  data-testid={`benefit-card-${i}`}
                  className="reveal group rounded-xl bg-white border border-[#E2E8F0] p-7 hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
                  style={{ transitionDelay: `${i * 60}ms` }}
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-[#EFF6FF] text-[#2563EB] group-hover:bg-[#2563EB] group-hover:text-white transition-colors">
                    <Icon className="h-6 w-6" />
                  </span>
                  <h3 className="mt-5 font-display text-lg font-bold text-[#1E293B]">
                    {b.title}
                  </h3>
                  <p className="mt-2 text-sm text-[#475569] leading-relaxed">
                    {b.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== ABOUT ===== */}
      <section id="about" className="py-20 md:py-28 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-20 items-center">
            <div className="reveal order-2 lg:order-1 relative">
              <img
                src={COMPANY.founderImage}
                alt={`${COMPANY.founder}, Founder of Coastal Valet Trash`}
                className="rounded-2xl shadow-2xl object-cover w-full h-[520px] bg-[#F1F5F9]"
              />
              <div className="absolute bottom-5 left-5 right-5 rounded-xl bg-white/95 backdrop-blur shadow-lg border border-gray-100 px-5 py-4">
                <div className="font-display text-lg font-bold text-[#1E293B]">
                  {COMPANY.founder}
                </div>
                <div className="text-sm text-[#2563EB] font-medium">
                  Founder & Owner
                </div>
              </div>
            </div>
            <div className="reveal order-1 lg:order-2">
              <SectionLabel>About Us</SectionLabel>
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#1E293B] leading-tight">
                Founded on reliability, built for professionalism
              </h2>
              <p className="mt-6 text-[#475569] leading-relaxed">
                Coastal Valet Trash was founded by{" "}
                <strong className="text-[#1E293B]">{COMPANY.founder}</strong>{" "}
                with a simple mission: bring dependable, professional valet
                trash service to apartment communities across the Savannah
                region. With hands-on industry experience in valet trash
                operations, Zuriel built a company that property managers can
                truly rely on.
              </p>
              <div className="mt-8 space-y-4">
                {[
                  "Industry experience in valet trash operations",
                  "Locally owned and operated in Savannah, GA",
                  "Unwavering commitment to reliability and professionalism",
                  "Personal accountability on every property we serve",
                ].map((point) => (
                  <div key={point} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 mt-0.5 text-[#2563EB] shrink-0" />
                    <span className="text-[#334155]">{point}</span>
                  </div>
                ))}
              </div>
              <a
                href="#contact"
                data-testid="about-cta-button"
                className="mt-9 inline-flex items-center gap-2 rounded-md bg-[#1E293B] px-6 py-3.5 text-sm font-semibold text-white hover:bg-[#0F172A] transition-colors"
              >
                Partner With Us <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ===== WHY CHOOSE US ===== */}
      <section id="why" className="py-20 md:py-28 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl reveal">
            <SectionLabel>Why Choose Us</SectionLabel>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#1E293B]">
              The dependable choice for Savannah communities
            </h2>
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-5">
            {WHY.map((w, i) => {
              const Icon = ICONS[w.icon];
              return (
                <div
                  key={w.title}
                  data-testid={`why-card-${i}`}
                  className="reveal rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                  style={{ transitionDelay: `${i * 60}ms` }}
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-sm text-[#2563EB]">
                    <Icon className="h-6 w-6" />
                  </span>
                  <h3 className="mt-5 font-display text-base font-bold text-[#1E293B]">
                    {w.title}
                  </h3>
                  <p className="mt-2 text-sm text-[#475569] leading-relaxed">
                    {w.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== SERVICE AREAS ===== */}
      <section id="areas" className="relative py-20 md:py-28">
        <div className="absolute inset-0">
          <img src={IMAGES.skyline} alt="Savannah skyline" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-[#0F172A]/90" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2 items-center">
            <div className="reveal">
              <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#60A5FA] mb-3">
                Service Areas
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-white">
                Proudly serving the greater Savannah region
              </h2>
              <p className="mt-5 text-slate-300 leading-relaxed max-w-lg">
                From the historic streets of Savannah to growing communities in
                Pooler and Richmond Hill, we deliver consistent valet trash
                service across coastal Georgia.
              </p>
            </div>
            <div className="reveal grid gap-4 sm:grid-cols-2">
              {AREAS.map((a, i) => (
                <div
                  key={a.name}
                  data-testid={`area-card-${i}`}
                  className="flex items-center gap-3 rounded-xl bg-white/[0.06] backdrop-blur border border-white/10 px-5 py-4 hover:bg-white/[0.12] transition-colors"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#2563EB] text-white shrink-0">
                    <MapPin className="h-5 w-5" />
                  </span>
                  <div>
                    <div className="font-display font-bold text-white">{a.name}</div>
                    <div className="text-xs text-slate-400">{a.state}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section id="testimonials" className="py-20 md:py-28 bg-[#F8FAFC]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl reveal">
            <SectionLabel>Testimonials</SectionLabel>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#1E293B]">
              Trusted by managers and residents
            </h2>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-2">
            {TESTIMONIALS.map((t, i) => (
              <div
                key={t.name}
                data-testid={`testimonial-card-${i}`}
                className="reveal rounded-2xl bg-white border border-[#E2E8F0] p-8 shadow-sm hover:shadow-lg transition-all duration-300"
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <div className="flex items-center justify-between">
                  <Quote className="h-9 w-9 text-[#2563EB]/25" />
                  <div className="flex gap-0.5">
                    {[...Array(5)].map((_, s) => (
                      <Star key={s} className="h-4 w-4 fill-[#F59E0B] text-[#F59E0B]" />
                    ))}
                  </div>
                </div>
                <p className="mt-5 text-[#334155] leading-relaxed">"{t.quote}"</p>
                <div className="mt-6 flex items-center gap-3 pt-5 border-t border-gray-100">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#1E3A8A] text-white font-display font-bold">
                    {t.name.charAt(0)}
                  </span>
                  <div>
                    <div className="font-display font-bold text-[#1E293B]">{t.name}</div>
                    <div className="text-sm text-[#475569]">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CONTACT ===== */}
      <section id="contact" className="py-20 md:py-28 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16 items-start">
            <div className="reveal lg:pt-6">
              <SectionLabel>Request a Free Proposal</SectionLabel>
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#1E293B] leading-tight">
                Let's keep your community clean
              </h2>
              <p className="mt-5 text-[#475569] leading-relaxed max-w-md">
                Tell us about your property and we'll put together a free,
                no-obligation valet trash proposal tailored to your community.
              </p>
              <div className="mt-9 space-y-5">
                <a href={`tel:${COMPANY.phonePrimaryRaw}`} className="flex items-center gap-4 group">
                  <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-[#EFF6FF] text-[#2563EB] group-hover:bg-[#2563EB] group-hover:text-white transition-colors">
                    <Phone className="h-5 w-5" />
                  </span>
                  <div>
                    <div className="text-sm text-[#475569]">Call us</div>
                    <div className="font-display font-bold text-[#1E293B]">
                      {COMPANY.phonePrimary} · {COMPANY.phoneSecondary}
                    </div>
                  </div>
                </a>
                <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-4 group">
                  <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-[#EFF6FF] text-[#2563EB] group-hover:bg-[#2563EB] group-hover:text-white transition-colors">
                    <Mail className="h-5 w-5" />
                  </span>
                  <div>
                    <div className="text-sm text-[#475569]">Email us</div>
                    <div className="font-display font-bold text-[#1E293B] break-all">
                      {COMPANY.email}
                    </div>
                  </div>
                </a>
              </div>
            </div>
            <div className="reveal">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
